import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { humanize, titleize } from 'underscore.string';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import { Plus } from 'lucide-react';
import Tooltip from 'components/v2/Tooltip';
import Badge from 'components/v2/Badge';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import ExclamationIcon from 'components/v2/icons/ExclamationIcon';
import { getEquipmentBadgeColor } from 'helpers/equipment';

const TOOLTIPS = {
  unavailable: 'EQUIPMENT_ATTACH_NOTE',
};

function renderIcon(params) {
  const { size, type } = params;
  return (
    <ExclamationIcon size={size} type={type} className="text-red w-5 h-5" />
  );
}

export default function EquipmentTable({
  equipments,
  availableEquipment,
  showAvailability,
  shipmentId,
  onRemove,
  onAdd,
}) {
  const equipmentIds = equipments.map((eq) => eq._id);
  const equipmentsToAdd = availableEquipment.filter(
    (eq) => !equipmentIds.includes(eq._id),
  );
  const { t } = useTranslation();

  const renderTypeCell = (equipment) => (
    <TableCell>
      <div className="flex items-center space-x-2">
        <Badge color={getEquipmentBadgeColor(equipment)}>
          {humanize(equipment._equipmentType)}
        </Badge>
        {showAvailability &&
          equipment.status === 'unavailable' &&
          equipment.shipment !== shipmentId && (
            <Tooltip
              id="equipment-unavailable"
              position="top"
              body={renderIcon}
            >
              {t(TOOLTIPS.unavailable)}
            </Tooltip>
          )}
      </div>
    </TableCell>
  );

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{titleize(t('TYPE'))}</TableHead>
            <TableHead>{titleize(t('EQUIPMENT_ID'))}</TableHead>
            <TableHead>{titleize(t('SIZE', { count: 1 }))}</TableHead>
            <TableHead>&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {equipments.map((equipment) => (
            <TableRow key={equipment._id}>
              {renderTypeCell(equipment)}
              <TableCell className="italic">{equipment.equipment_id}</TableCell>
              <TableCell>
                {equipment._equipmentSize
                  ? `${equipment._equipmentSize}'`
                  : null}
              </TableCell>
              <TableCell>
                {equipment.canRemove && (
                  <DeleteButton onClick={() => onRemove(equipment)} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {equipmentsToAdd.length > 0 && (
        <div className="mt-4">
          <h4>{t('AVAILABLE_EQUIPMENT')}</h4>
          <Table className="mt-2">
            <TableHeader>
              <TableRow>
                <TableHead>{titleize(t('TYPE'))}</TableHead>
                <TableHead>{titleize(t('EQUIPMENT_ID'))}</TableHead>
                <TableHead>{titleize(t('SIZE', { count: 1 }))}</TableHead>
                <TableHead>&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {equipmentsToAdd.map((equipment) => (
                <TableRow key={equipment._id}>
                  {renderTypeCell(equipment)}
                  <TableCell className="italic">
                    {equipment.equipment_id}
                  </TableCell>
                  <TableCell>
                    {equipment._equipmentSize
                      ? `${equipment._equipmentSize}'`
                      : null}
                  </TableCell>
                  <TableCell>
                    {equipment.canAdd && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-green-600 hover:text-green-700"
                        title={titleize(t('ADD'))}
                        onClick={() => onAdd(equipment)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

EquipmentTable.propTypes = {
  equipments: PropTypes.array,
  availableEquipment: PropTypes.array,
  shipmentId: PropTypes.string.isRequired,
  showAvailability: PropTypes.bool,
  onAdd: PropTypes.func,
  onRemove: PropTypes.func,
};

EquipmentTable.defaultProps = {
  equipments: [],
  availableEquipment: [],
  showAvailability: false,
  onAdd: () => {},
  onRemove: () => {},
};
